import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';

export interface VerificationResult {
  success: boolean;
  message: string;
  remainingAttempts?: number;
}

export interface EmailVerificationState {
  email: string;
  isVerified: boolean;
  verificationSent: boolean;
  verificationCode: string;
  sentAt: Date | null;
  attempts: number;
  maxAttempts: number;
  cooldownUntil: Date | null;
}

@Injectable({
  providedIn: 'root'
})
export class EmailVerificationService {
  private verificationStateSubject = new BehaviorSubject<EmailVerificationState>({
    email: '',
    isVerified: false,
    verificationSent: false,
    verificationCode: '',
    sentAt: null,
    attempts: 0,
    maxAttempts: 3,
    cooldownUntil: null
  });

  verificationState$ = this.verificationStateSubject.asObservable();

  constructor() {}

  sendVerificationCode(email: string): Observable<VerificationResult> {
    const currentState = this.verificationStateSubject.value;

    // Check cooldown period
    if (currentState.cooldownUntil && new Date() < currentState.cooldownUntil) {
      const remainingTime = Math.ceil((currentState.cooldownUntil.getTime() - new Date().getTime()) / 1000);
      return throwError(() => ({
        success: false,
        message: `Please wait ${remainingTime} seconds before requesting another code.`
      }));
    }

    // Generate 6-digit verification code
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
    const sentAt = new Date();

    // Set cooldown period (60 seconds)
    const cooldownUntil = new Date(sentAt.getTime() + 10000);

    // Update state
    this.verificationStateSubject.next({
      ...currentState,
      email,
      verificationCode,
      verificationSent: true,
      sentAt,
      cooldownUntil,
      attempts: 0 // Reset attempts when new code is sent
    });

    // Simulate API call with delay
    return of({
      success: true,
      message: ''
    }).pipe(delay(1500)); // Simulate network delay
  }

  verifyCode(inputCode: string): Observable<VerificationResult> {
    const currentState = this.verificationStateSubject.value;

    if (!currentState.verificationSent) {
      return throwError(() => ({
        success: false,
        message: 'No verification code has been sent. Please request a new code.'
      }));
    }

    // Check if code has expired (10 minutes)
    if (currentState.sentAt) {
      const expirationTime = new Date(currentState.sentAt.getTime() + 10 * 60000); // 10 minutes
      if (new Date() > expirationTime) {
        this.verificationStateSubject.next({
          ...currentState,
          verificationSent: false,
          verificationCode: '',
          sentAt: null
        });

        return throwError(() => ({
          success: false,
          message: 'Verification code has expired. Please request a new code.'
        }));
      }
    }

    const newAttempts = currentState.attempts + 1;

    // Check max attempts
    if (newAttempts > currentState.maxAttempts) {
      // Lock verification for 5 minutes
      const lockUntil = new Date(new Date().getTime() + 5 * 60000);

      this.verificationStateSubject.next({
        ...currentState,
        attempts: newAttempts,
        cooldownUntil: lockUntil,
        verificationSent: false,
        verificationCode: ''
      });

      return throwError(() => ({
        success: false,
        message: 'Too many failed attempts. Please wait 5 minutes before trying again.'
      }));
    }

    // Verify code
    if (inputCode === currentState.verificationCode) {
      this.verificationStateSubject.next({
        ...currentState,
        isVerified: true,
        attempts: newAttempts
      });

      return of({
        success: true,
        message: ''
      }).pipe(delay(500));
    } else {
      this.verificationStateSubject.next({
        ...currentState,
        attempts: newAttempts
      });

      const remainingAttempts = currentState.maxAttempts - newAttempts;
      return throwError(() => ({
        success: false,
        message: `Invalid verification code. ${remainingAttempts} attempts remaining.`,
        remainingAttempts
      }));
    }
  }

  resendVerificationCode(): Observable<VerificationResult> {
    const currentState = this.verificationStateSubject.value;

    if (!currentState.email) {
      return throwError(() => ({
        success: false,
        message: 'No email address found. Please enter your email first.'
      }));
    }

    return this.sendVerificationCode(currentState.email);
  }

  resetVerification(): void {
    this.verificationStateSubject.next({
      email: '',
      isVerified: false,
      verificationSent: false,
      verificationCode: '',
      sentAt: null,
      attempts: 0,
      maxAttempts: 3,
      cooldownUntil: null
    });
  }

  isEmailVerified(email: string): boolean {
    const currentState = this.verificationStateSubject.value;
    return currentState.email === email && currentState.isVerified;
  }

  getVerificationStatus(): EmailVerificationState {
    return this.verificationStateSubject.value;
  }

  // For development/testing - shows the current verification code
  getCurrentVerificationCode(): string {
    return this.verificationStateSubject.value.verificationCode;
  }

  // Check if cooldown is active
  isCooldownActive(): boolean {
    const currentState = this.verificationStateSubject.value;
    return currentState.cooldownUntil ? new Date() < currentState.cooldownUntil : false;
  }

  // Get remaining cooldown time in seconds
  getRemainingCooldownTime(): number {
    const currentState = this.verificationStateSubject.value;
    if (!currentState.cooldownUntil) return 0;

    const remaining = Math.ceil((currentState.cooldownUntil.getTime() - new Date().getTime()) / 1000);
    return Math.max(0, remaining);
  }
}
